import { App, Avatar, Button, Select, Spin } from 'antd';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type Group from '../../../interfaces/Group';
import type { GetGroupsPayload } from '../../../interfaces/GroupApiService';
import type User from '../../../interfaces/User';
import type { GetUsersPayload } from '../../../interfaces/UserApiService';
import { UserRoleEnum } from '../../../enums/UserRoleEnum';
import { GroupService } from '../../../services/groupApiService';
import { UserService } from '../../../services/userApiService';
import { getAvatarColor } from '../../../utils/avatarColors';
import { validateEmail } from '../../../utils/utils';
import { Icon } from '../../Core/Icon';
import TruncatedTooltip from '../TruncatedTooltip';

export interface UserMultiSelectProps {
  label?: string;
  mode?: 'multiple' | 'tags';
  description?: string | null;
  errorMessage?: string;
  className?: string;
  includeUsers?: boolean;
  includeGroups?: boolean;
  disabled?: boolean;
  placeholder?: string;
  selectedUserIds?: number[];
  selectedGroupIds?: number[];
  onUserChange?: (ids: number[], values: User[]) => void;
  onGroupChange?: (ids: number[], values: Group[]) => void;
  onReset?: () => void;
  additionalUserFilters?: Partial<GetUsersPayload>;
  additionalGroupFilters?: Partial<GetGroupsPayload>;
  allowClear?: boolean;
  maxTagCount?: number;
  closeOnSelect?: boolean;
  size?: 'large' | 'middle' | 'small';
  selectClassName?: string;
  ignoredUserIds?: number[];
  ignoredGroupIds?: number[];
  /** Roles that appear in the list but cannot be selected (e.g. SuperAdmin for Admins). */
  disabledUserRoles?: UserRoleEnum[];
  /** Message shown when a role-disabled user is clicked / hovered. */
  disabledRoleMessage?: string;
  forbiddenTagNames?: string[];
  variant?: 'outlined' | 'borderless' | 'filled' | 'underlined';
  selectedUsers?: User[];
  selectedGroups?: Group[];
}

const userService = new UserService();
const groupService = new GroupService();

interface DataMap {
  [key: string]: User | Group;
}

export default function UserMultiSelect(props: UserMultiSelectProps) {
  const {
    label,
    description,
    errorMessage,
    className,
    placeholder = 'Search for users or groups',
    includeUsers = true,
    includeGroups = true,
    disabled,
    selectedUserIds = [],
    selectedGroupIds = [],
    onUserChange,
    onGroupChange,
    onReset,
    additionalUserFilters,
    additionalGroupFilters,
    allowClear = true,
    maxTagCount = 0,
    closeOnSelect = false,
    size = 'middle',
    selectClassName = 'w-full',
    mode = 'multiple',
    ignoredUserIds = [],
    ignoredGroupIds = [],
    disabledUserRoles = [],
    disabledRoleMessage,
    forbiddenTagNames = [],
    variant = 'outlined',
    selectedUsers = [],
    selectedGroups = [],
  } = props;

  const { notification: notificationApi } = App.useApp();
  const { t } = useTranslation();

  const isUserRoleDisabled = useCallback(
    (user: User | undefined | null) =>
      Boolean(user?.role != null && disabledUserRoles.includes(user.role)),
    [disabledUserRoles],
  );

  // Data and pagination states
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalGroups, setTotalGroups] = useState(0);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Selected values and data lookup map
  const [values, setValues] = useState<string[]>([]);
  const [dataMap, setDataMap] = useState<DataMap>({});

  // Filters for server-side search
  const [userFilters, setUserFilters] = useState<GetUsersPayload>({
    count: 15,
    offset: 0,
    search: '',
    ...(additionalUserFilters || {}),
  });
  const [groupFilters, setGroupFilters] = useState<GetGroupsPayload>({
    count: 15,
    offset: 0,
    search: '',
    ...(additionalGroupFilters || {}),
  });
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Ref for whether the dropdown should remain open
  const shouldKeepOpenRef = useRef<boolean>(false);

  /**
   * Fetch users (ignored users are included but will be marked as disabled in options)
   */
  const fetchUsers = useCallback(
    async (filters: GetUsersPayload, reset = false) => {
      setIsLoadingUsers(true);
      try {
        const res = await userService.getUsers(filters);
        const raw = res.users || [];
        // Include all users (ignored ones will be disabled in the UI)
        setUsers((prev) => (reset ? raw : [...prev, ...raw]));
        setTotalUsers((prevTotal) => (reset ? (res.total ?? raw.length) : prevTotal));
      } catch {
        notificationApi.error({
          title: t('errors.genericFetchError', {
            resource: t('common.users'),
          }),
        });
      } finally {
        setIsLoadingUsers(false);
        // Keep dropdown open if we're still searching
        if (filters.search?.trim() && shouldKeepOpenRef.current) {
          setIsOpen(true);
        }
      }
    },
    [ignoredUserIds, notificationApi, t],
  );

  /**
   * Fetch groups (ignored groups are included but will be marked as disabled in options)
   */
  const fetchGroups = useCallback(
    async (filters: GetGroupsPayload, reset = false) => {
      setIsLoadingGroups(true);
      try {
        const res = await groupService.getGroups(filters);
        const raw = res.groups || [];
        // Include all groups (ignored ones will be disabled in the UI)
        setGroups((prev) => (reset ? raw : [...prev, ...raw]));
        setTotalGroups((prevTotal) => (reset ? (res.total ?? raw.length) : prevTotal));
      } catch {
        notificationApi.error({
          title: t('errors.genericFetchError', {
            resource: t('common.groups'),
          }),
        });
      } finally {
        setIsLoadingGroups(false);
        // Keep dropdown open if we're still searching
        if (filters.search?.trim() && shouldKeepOpenRef.current) {
          setIsOpen(true);
        }
      }
    },
    [ignoredGroupIds, notificationApi, t],
  );

  // Infinite scroll load more
  const handleLoadMore = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.target as HTMLDivElement;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 10) return;

    if (includeUsers && users.length < totalUsers && !isLoadingUsers) {
      const uf = {
        ...userFilters,
        offset: userFilters.offset + userFilters.count,
      };
      setUserFilters(uf);
      void fetchUsers(uf, false);
    }
    if (includeGroups && groups.length < totalGroups && !isLoadingGroups) {
      const gf = {
        ...groupFilters,
        offset: groupFilters.offset + groupFilters.count,
      };
      setGroupFilters(gf);
      void fetchGroups(gf, false);
    }
  };

  // Debounced search
  const handleSearch = (keyword: string) => {
    shouldKeepOpenRef.current = true;
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      const uf = { ...userFilters, search: keyword, offset: 0 };
      const gf = { ...groupFilters, search: keyword, offset: 0 };
      setUserFilters(uf);
      setGroupFilters(gf);
      if (includeUsers) void fetchUsers(uf, true);
      if (includeGroups) void fetchGroups(gf, true);
    }, 300);
    setDebounceTimer(timer);
  };

  // Build raw options from fetched data
  const rawOptions = useMemo(() => {
    const groupOpts = groups.map((g) => {
      const isIgnored = ignoredGroupIds.includes(g.id);
      return {
        value: `group-${g.id}`,
        data: g,
        disabled: isIgnored,
        label: (
          <div className={clsx('flex items-center gap-2', isIgnored && 'opacity-50')}>
            <Avatar
              size={32}
              className="text-blue-dark!"
              style={{ backgroundColor: getAvatarColor(g.id) }}
            >
              <Icon name="UsersIcon" className="h-4 w-4" />
            </Avatar>
            <div className="flex flex-col">
              <span className="text-blue-dark">{g.name}</span>
              <span className="text-gray-dark text-sm">{g.usersCount} members</span>
            </div>
            {isIgnored && (
              <Icon name="CircleCheckIcon" className="text-green-dark ml-auto h-4 w-4 shrink-0" />
            )}
          </div>
        ),
      };
    });

    const userOpts = users.map((u) => {
      const isIgnored = ignoredUserIds.includes(u.id);
      const isRoleDisabled = isUserRoleDisabled(u);
      const isDisabled = isIgnored || isRoleDisabled;
      return {
        value: `user-${u.id}`,
        data: u,
        disabled: isDisabled,
        label: (
          <div
            className={clsx('flex items-center gap-2 truncate', isDisabled && 'opacity-50')}
            title={isRoleDisabled ? disabledRoleMessage : undefined}
          >
            <Avatar
              size={32}
              className="text-blue-dark! min-w-8!"
              style={{ backgroundColor: getAvatarColor(u.id) }}
            >
              {u.firstName || u.lastName ? (
                <span className="flex items-center justify-center text-sm">
                  {u.firstName?.[0]?.toUpperCase()}
                  {u.lastName?.[0]?.toUpperCase()}
                </span>
              ) : (
                <Icon name="AtSignIcon" className="h-5 w-5" />
              )}
            </Avatar>
            <div className="flex flex-col truncate text-sm">
              <span className="text-blue-dark truncate">
                <TruncatedTooltip>
                  {u.firstName} {u.lastName}
                </TruncatedTooltip>
              </span>
              <span className="text-gray-dark truncate text-sm">
                <TruncatedTooltip>{u.email}</TruncatedTooltip>
              </span>
            </div>
            {isIgnored && (
              <Icon name="UserCheckIcon" className="text-green-dark ml-auto h-4 w-4 shrink-0" />
            )}
            {isRoleDisabled && !isIgnored && (
              <Icon name="BanIcon" className="text-gray-dark ml-auto h-4 w-4 shrink-0" />
            )}
          </div>
        ),
      };
    });

    return [...groupOpts, ...userOpts];
  }, [groups, users, ignoredUserIds, ignoredGroupIds, isUserRoleDisabled, disabledRoleMessage]);

  // Ensure selected values remain in options after search
  const mergedOptions = useMemo(() => {
    const map = new Map<string, any>(rawOptions.map((o) => [o.value, o]));
    values.forEach((v) => {
      if (!map.has(v) && dataMap[v]) {
        const item = dataMap[v];
        const isUser = v.startsWith('user-');
        const id = isUser
          ? parseInt(v.replace('user-', ''), 10)
          : parseInt(v.replace('group-', ''), 10);
        const isIgnored = isUser ? ignoredUserIds.includes(id) : ignoredGroupIds.includes(id);
        const isRoleDisabled = isUser ? isUserRoleDisabled(item as User) : false;

        map.set(v, {
          value: v,
          data: item,
          disabled: isIgnored || isRoleDisabled,
          label: isUser
            ? (item as User)?.firstName || (item as User)?.lastName
              ? `${(item as User)?.firstName} ${(item as User)?.lastName}`
              : (item as User)?.email
            : (item as Group).name,
        });
      }
    });

    return Array.from(map.values());
  }, [rawOptions, values, dataMap, ignoredUserIds, ignoredGroupIds, isUserRoleDisabled]);

  /**
   * Handles the change event for the user multi select.
   * @param newVals The new values that are being added
   * @param selectedOptions The selected options
   * @returns Whether the change was successful
   *
   * Dependencies:
   * - `notificationApi`: The notification API
   * - `t`: The translation function
   * - `values`: The current values
   * - `ignoredUserIds`: The ignored user IDs
   * - `ignoredGroupIds`: The ignored group IDs
   * - `forbiddenTagNames`: The forbidden tag names
   */
  const handleChange = (newVals: string[], selectedOptions: any | any[]) => {
    // Determine which values are being added (not already in current values)
    const addedVals = newVals.filter((v) => !values.includes(v));

    // Only check forbidden tags for newly added external tags
    const externalTags = addedVals.filter((v) => !v.startsWith('user-') && !v.startsWith('group-'));

    const forbiddenExternalTags = externalTags.filter((v) => forbiddenTagNames.includes(v));

    if (forbiddenExternalTags.length) {
      notificationApi.warning({
        title: t('errors.someItemsCannotBeSelected'),
      });
      return false;
    }

    // For safety, check if the newly ADDED values are allowed (not for removed ones)
    const forbidden = addedVals.filter((v) => {
      if (v.startsWith('user-')) {
        const id = parseInt(v.replace('user-', ''), 10);
        if (ignoredUserIds.includes(id)) return true;
        const fromMap = dataMap[v] as User | undefined;
        const fromOpts = (
          Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions]
        ).find((o) => o?.value === v)?.data as User | undefined;
        return isUserRoleDisabled(fromMap || fromOpts);
      }
      if (v.startsWith('group-')) {
        const id = parseInt(v.replace('group-', ''), 10);
        return ignoredGroupIds.includes(id);
      }
      return false;
    });

    let filtered = newVals;
    if (forbidden.length) {
      notificationApi.warning({
        title: disabledRoleMessage || t('errors.someItemsCannotBeSelected'),
      });
      // Remove only the forbidden items that were being added
      filtered = newVals.filter((v) => !forbidden.includes(v));
    }

    // Build updated map: keep existing items from dataMap that are still in filtered,
    // and add/update items from selectedOptions
    const opts = Array.isArray(selectedOptions)
      ? selectedOptions.filter((o) => o && o.value && o.data)
      : selectedOptions && selectedOptions.value && selectedOptions.data
        ? [selectedOptions]
        : [];
    const newMap: DataMap = {};

    // First, keep all existing items from dataMap that are still selected
    // This ensures we have all the data for remaining items even if selectedOptions is empty
    filtered.forEach((val) => {
      if (dataMap[val]) {
        newMap[val] = dataMap[val];
      }
    });

    // Then, update/add items from selectedOptions (for newly added items)
    opts.forEach((o) => {
      if (o && o.value && o.data) {
        newMap[o.value] = o.data;
      }
    });

    // Extract IDs
    const uids = filtered
      .filter((v) => v.startsWith('user-'))
      .map((v) => parseInt(v.replace('user-', ''), 10));
    const gids = filtered
      .filter((v) => v.startsWith('group-'))
      .map((v) => parseInt(v.split('-')[1] ?? '', 10));

    const externalUsers = filtered.filter(
      (v) => !(v.startsWith('user-') || v.startsWith('group-')),
    );

    // Track converted values to update filtered array
    const convertedValues: string[] = [];

    if (mode === 'tags' && externalUsers.length > 0) {
      externalUsers.forEach((email, index) => {
        const normalizedEmail = email.toLowerCase().trim();

        // 1. Check if this email is already selected (as a user-{id} key) — prevent duplicates
        const alreadySelectedKey = filtered.find((valueKey) => {
          if (!valueKey.startsWith('user-')) return false;
          const existingUser = newMap[valueKey] || dataMap[valueKey];
          return (
            existingUser &&
            'email' in existingUser &&
            (existingUser as User).email?.toLowerCase() === normalizedEmail
          );
        });

        if (alreadySelectedKey) {
          const existingId = parseInt(alreadySelectedKey.replace('user-', ''), 10);
          if (!Number.isNaN(existingId) && !uids.includes(existingId)) {
            uids.push(existingId);
          }
          return;
        }

        // 2. Check if an exact match exists in the dataMap/newMap
        const entryInMap = Object.entries(newMap).find(
          ([, val]) =>
            val &&
            'email' in (val as User) &&
            (val as User).email?.toLowerCase() === normalizedEmail,
        );

        if (entryInMap) {
          const [existingKey] = entryInMap;
          if (!newVals.includes(existingKey)) {
            newVals.push(existingKey);
          }
          if (!convertedValues.includes(existingKey) && !filtered.includes(existingKey)) {
            convertedValues.push(existingKey);
          }
          const id = parseInt(existingKey.replace('user-', ''), 10);
          if (!Number.isNaN(id) && !uids.includes(id)) uids.push(id);
          return;
        }

        // 3. Check if an exact match exists in the fetched search results (users state)
        const matchedUser = users.find((u) => u.email?.toLowerCase() === normalizedEmail);

        if (matchedUser) {
          const key = `user-${matchedUser.id}`;
          newMap[key] = matchedUser;
          if (!newVals.includes(key)) {
            newVals.push(key);
          }
          if (!convertedValues.includes(key) && !filtered.includes(key)) {
            convertedValues.push(key);
          }
          if (!uids.includes(matchedUser.id)) uids.push(matchedUser.id);
          return;
        }

        // 4. No match found anywhere: create a new entry with negative id
        if (validateEmail(email)) {
          const existingNegativeIds = new Set(uids.filter((userId) => userId < 0));
          let extId = -(
            index +
            (uids.filter((userId) => userId > 0).length + existingNegativeIds.size + 1)
          );
          while (existingNegativeIds.has(extId)) {
            extId -= 1;
          }
          const key = `user-${extId}`;
          newMap[key] = { id: extId, email } as Partial<User> as User;
          if (!newVals.includes(key)) {
            newVals.push(key);
          }
          if (!convertedValues.includes(key) && !filtered.includes(key)) {
            convertedValues.push(key);
          }
          if (!uids.includes(extId)) {
            uids.push(extId);
          }
        }
      });
    }

    // Build final list of selected values: existing user/group keys + converted email keys
    const finalSelectedValues = [
      ...new Set([
        ...filtered.filter(
          (valueKey) => valueKey.startsWith('user-') || valueKey.startsWith('group-'),
        ),
        ...convertedValues,
      ]),
    ];

    const selectedSet = new Set(finalSelectedValues);

    // keep in the map only what is selected, but ensure we have all data from both newMap and dataMap
    const prunedMap: DataMap = {};
    // First, add all items from newMap that are selected
    for (const [k, v] of Object.entries(newMap)) {
      if (selectedSet.has(k)) {
        prunedMap[k] = v;
      }
    }
    // Then, add any missing items from dataMap (fallback for items not in newMap)
    for (const [k, v] of Object.entries(dataMap)) {
      if (selectedSet.has(k) && !prunedMap[k]) {
        prunedMap[k] = v;
      }
    }

    // Update state - use finalSelectedValues which includes converted email keys
    setValues(finalSelectedValues);
    setDataMap(prunedMap);

    if (uids.length === 0 && gids.length == 0 && onReset) {
      onReset();
    } else {
      // Resolve entities from prunedMap (merged selection + dataMap), then current search results.
      // Ant Design may not pass custom `data` on selectedOptions; newMap alone can miss new picks.
      const resolveUserById = (id: number): User | undefined => {
        const key = `user-${id}`;
        return (prunedMap[key] as User | undefined) ?? users.find((u) => u.id === id);
      };
      const resolveGroupById = (id: number): Group | undefined => {
        const key = `group-${id}`;
        return (prunedMap[key] as Group | undefined) ?? groups.find((g) => g.id === id);
      };

      const resolvedUsers = uids
        .filter((userId, index, allUserIds) => allUserIds.indexOf(userId) === index)
        .map((id) => resolveUserById(id))
        .filter((user): user is User => Boolean(user));
      const resolvedGroups = gids
        .map((id) => resolveGroupById(id))
        .filter((g): g is Group => Boolean(g));

      // Call each callback only when its dimension actually changed.
      // This prevents parent handlers from overwriting each other with stale external state.
      const previousUserIds = values
        .filter((value) => value.startsWith('user-'))
        .map((value) => parseInt(value.replace('user-', ''), 10));
      const previousGroupIds = values
        .filter((value) => value.startsWith('group-'))
        .map((value) => parseInt(value.replace('group-', ''), 10));
      const didUsersChange =
        previousUserIds.length !== uids.length ||
        previousUserIds.some((id, index) => id !== uids[index]);
      const didGroupsChange =
        previousGroupIds.length !== gids.length ||
        previousGroupIds.some((id, index) => id !== gids[index]);

      if (includeUsers && onUserChange && didUsersChange) {
        onUserChange(uids, resolvedUsers);
      }
      if (includeGroups && onGroupChange && didGroupsChange) {
        onGroupChange(gids, resolvedGroups);
      }
    }

    if (closeOnSelect) {
      setIsOpen(false);
    }
  };

  // Unified selection sync: objects take priority; fallback to IDs for retro-compatibility
  useEffect(() => {
    // Keys derived from objects (preferred)
    const objectKeys: string[] = [
      ...(selectedUsers ?? []).filter((u) => typeof u?.id === 'number').map((u) => `user-${u.id}`),
      ...(selectedGroups ?? [])
        .filter((g) => typeof g?.id === 'number')
        .map((g) => `group-${g.id}`),
    ];

    // Keys derived from IDs (legacy/retro-compat)
    const idKeys: string[] = [
      ...(selectedUserIds ?? []).map((id) => `user-${id}`),
      ...(selectedGroupIds ?? []).map((id) => `group-${id}`),
    ];

    // 1) Seed/merge dataMap only from objects (so labels render when provided)
    if (objectKeys.length) {
      setDataMap((prev) => {
        let changed = false;
        const next: DataMap = { ...prev };

        for (const u of selectedUsers ?? []) {
          if (!u || typeof u.id !== 'number') continue;
          const k = `user-${u.id}`;
          if (next[k] !== u) {
            next[k] = u;
            changed = true;
          }
        }
        for (const g of selectedGroups ?? []) {
          if (!g || typeof g.id !== 'number') continue;
          const k = `group-${g.id}`;
          if (next[k] !== g) {
            next[k] = g;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }

    // 2) Drive `values`: prefer objects; otherwise fall back to IDs (backward compatible)
    const driveKeys = objectKeys.length ? objectKeys : idKeys;

    setValues((previousValues) => {
      const sameSelection =
        previousValues.length === driveKeys.length &&
        driveKeys.every((key) => previousValues.includes(key));
      return sameSelection ? previousValues : driveKeys;
    });
  }, [selectedUsers, selectedGroups, selectedUserIds, selectedGroupIds]);

  // Initial data load
  useEffect(() => {
    if (isOpen && !initialized) {
      shouldKeepOpenRef.current = true;
      if (includeUsers) {
        void fetchUsers(userFilters, true);
      }
      if (includeGroups) {
        void fetchGroups(groupFilters, true);
      }
      setInitialized(true);
    }
  }, [isOpen, initialized]);

  const loading = isLoadingUsers || isLoadingGroups;

  /**
   * Re-open the dropdown after loading, if search keyword and 'keep open' ref are set.
   * Small delay (50ms) to avoid fight with Ant Design popup close timing.
   */
  useEffect(() => {
    if (
      !loading &&
      (userFilters.search?.trim() || groupFilters.search?.trim()) &&
      shouldKeepOpenRef.current
    ) {
      const timeoutId = setTimeout(() => {
        if (
          (userFilters.search?.trim() || groupFilters.search?.trim()) &&
          shouldKeepOpenRef.current
        ) {
          setIsOpen(true);
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [loading, userFilters.search, groupFilters.search]);
  const finalPlaceholder =
    values.length > 0 ? `${values.length} ${t('common.selected')}` : placeholder;

  return (
    <div className={className}>
      {label && <label>{label}</label>}
      {description && <p className="text-gray-dark text-sm">{description}</p>}
      <Select<string[]>
        className={selectClassName}
        mode={mode}
        variant={variant}
        size={size}
        open={isOpen}
        value={values}
        disabled={disabled}
        allowClear={allowClear}
        maxTagCount={maxTagCount}
        maxTagPlaceholder={(omitted) => `+${omitted.length} selected`}
        placeholder={finalPlaceholder}
        loading={loading}
        showSearch={{
          onSearch: handleSearch,
          filterOption: false,
        }}
        options={mergedOptions}
        onOpenChange={(open) => {
          // Prevent closing if loading, allow otherwise
          if (!open && loading) {
            // Schedule re-open if still loading, prevents accidental closure
            requestAnimationFrame(() => {
              if (loading) {
                setIsOpen(true);
              }
            });
            return;
          }

          if (!open) {
            shouldKeepOpenRef.current = false;
          }

          setIsOpen(open);
        }}
        onPopupScroll={handleLoadMore}
        onChange={handleChange}
        tagRender={(props) => {
          if (props.isMaxTag) {
            return (
              <div className="ant-select-selection-overflow-item ant-select-selection-overflow-item-rest">
                <span title={`${props.label}`} className="ant-select-selection-item">
                  <span className="ant-select-selection-item-content">{props.label}</span>
                </span>
              </div>
            );
          }

          const { value, onClose } = props;
          const item = dataMap?.[value];
          const isUser = value?.startsWith('user-');

          const itemId = isUser ? (item as User)?.id || 0 : (item as Group)?.id || 0;

          return (
            <div className="m-0.5 flex items-center gap-1 rounded border p-1">
              <Avatar
                size={24}
                className="text-blue-dark! shrink-0! text-center! text-sm! uppercase!"
                style={{ backgroundColor: getAvatarColor(itemId) }}
              >
                {isUser ? (
                  <>
                    {(item as User)?.firstName || (item as User)?.lastName ? (
                      <span className="flex items-center justify-center text-xs">
                        {(item as User)?.firstName?.[0]?.toUpperCase()}
                        {(item as User)?.lastName?.[0]?.toUpperCase()}
                      </span>
                    ) : (
                      <Icon name="AtSignIcon" />
                    )}
                  </>
                ) : (
                  <Icon name="UsersIcon" />
                )}
              </Avatar>
              <span className="text-blue-dark flex items-center justify-center text-xs">
                {isUser
                  ? (item as User)?.firstName || (item as User)?.lastName
                    ? `${(item as User)?.firstName} ${(item as User)?.lastName}`
                    : (item as User)?.email
                  : (item as Group)?.name}
              </span>
              <Button
                disabled={disabled}
                type="text"
                size="small"
                aria-label={t('common.close')}
                onClick={onClose}
              >
                <Icon name="XIcon" className="text-gray-dark h-4 w-4" />
              </Button>
            </div>
          );
        }}
        notFoundContent={
          loading ? (
            <Spin size="small" />
          ) : (
            <div className="text-gray-dark py-1 text-center text-sm">{t('common.noResults')}</div>
          )
        }
      />
      {errorMessage && <p className="text-red mt-1">{errorMessage}</p>}
    </div>
  );
}
